import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  PageContainer,
  Card,
  Button,
  Badge,
  DataTable,
  Skeleton,
  EmptyState,
  ErrorBoundary,
  HeatmapChart,
  InventoryValueWidget,
} from '@bbpos/ui';
import type { ColumnDef, HeatmapCell, InventoryValueCategory } from '@bbpos/ui';
import { formatPaise } from '@/utils/currency';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { productService } from '@/services/product.service';
import { useDashboardStore } from '@/stores/use-dashboard-store';
import type { DeadStockItem, StockMovement } from '@/types/product';

type MovementRow = StockMovement & { id: string };

// Category color palette for InventoryValueWidget
const CATEGORY_COLORS = [
  '#0d9488', '#10b981', '#6366f1', '#8b5cf6',
  '#f59e0b', '#ef4444', '#3b82f6', '#ec4899',
];

/**
 * Build the "Stock Value by Category" breakdown from the server-side, dataset-wide
 * `summary.category_breakdown` — NOT from the capped, paginated `data` page. Sourcing
 * it from the whole-catalog aggregate keeps the category totals reconciled with the
 * headline Stock Value KPI (`summary.total_value`) even when a tenant/location has more
 * products than one valuation page can hold. See GitHub issue #217.
 */
export function deriveInventoryCategories(
  categoryBreakdown?: { category: string; total_value: number }[],
): InventoryValueCategory[] {
  return (categoryBreakdown ?? [])
    .slice()
    .sort((a, b) => b.total_value - a.total_value)
    .slice(0, 8)
    .map((c, i) => ({
      label: c.category || 'Uncategorized',
      value: c.total_value,
      color: CATEGORY_COLORS[i % CATEGORY_COLORS.length],
    }));
}

/**
 * Drill-down targets for the two adjacent stock-alert KPI cards. They must NOT be
 * the same URL: the "Out of Stock" card carries a `filter=out-of-stock` query so
 * its destination is scoped to the zero-stock SKUs it counts, rather than dumping
 * the user into the unfiltered low-stock list. See GitHub issue #219.
 */
export const LOW_STOCK_KPI_TARGET = '/inventory/low-stock';
export const OUT_OF_STOCK_KPI_TARGET = '/inventory/low-stock?filter=out-of-stock';

/**
 * Derive the Low Stock / Out of Stock KPI counts from the server-side totals of
 * two stock-status-filtered product queries. Reading `meta.total` (the true,
 * unbounded count) — rather than filtering a capped page of rows client-side —
 * keeps both figures accurate even when a tenant/location has more low/out-of-
 * stock SKUs than a single page can hold. See GitHub issue #216.
 */
export function deriveStockKpiCounts(
  lowStock?: { meta?: { total?: number } },
  outOfStock?: { meta?: { total?: number } },
): { lowStockCount: number; oosCount: number } {
  return {
    lowStockCount: lowStock?.meta?.total ?? 0,
    oosCount: outOfStock?.meta?.total ?? 0,
  };
}

/**
 * Decide which view the dashboard should render from the five query states.
 *
 * Every query's error was previously ignored: a failed products-count left
 * `total` at 0 and rendered the "No inventory data" empty state (a false
 * "you have no stock" message), while a failed valuation/movements call
 * silently rendered ₹0 / an empty heatmap. This collapses the five states into
 * an explicit status so an error is surfaced (with a retry) instead of masqueraded
 * as legitimate "empty" or "zero" data. Order matters: loading first, then any
 * error, then genuine empty (products succeeded with 0), then ready.
 * See GitHub issue #218.
 */
export type InventoryDashboardStatus = 'loading' | 'error' | 'empty' | 'ready';

export function deriveInventoryDashboardStatus(input: {
  isLoading: boolean;
  isError: boolean;
  productsTotal: number;
}): InventoryDashboardStatus {
  if (input.isLoading) return 'loading';
  if (input.isError) return 'error';
  return input.productsTotal > 0 ? 'ready' : 'empty';
}

/**
 * Slow-moving stock (FR-PROD-029 / US-INV-020): products still holding stock with no
 * completed sale in the last DEAD_STOCK_DAYS. The API already sorts by stock_value
 * DESC; this totals the capital locked in them and keeps the top rows for the table.
 * QA 6.11.7 — the report was computed server-side but no screen showed it.
 */
export const DEAD_STOCK_DAYS = 30;

export function deriveDeadStockSummary(
  rows?: DeadStockItem[],
  top = 10,
): { count: number; totalValue: number; topRows: DeadStockItem[] } {
  const list = rows ?? [];
  return {
    count: list.length,
    totalValue: list.reduce((sum, r) => sum + Number(r.stock_value ?? 0), 0),
    topRows: list.slice(0, top),
  };
}

const movementColumns: ColumnDef<MovementRow>[] = [
  {
    key: 'product_name',
    header: 'Product',
    render: (_v, row) => (
      <div>
        <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{row.product_name}</p>
        <p className="text-xs text-neutral-400 dark:text-neutral-500">{row.sku}</p>
      </div>
    ),
  },
  {
    key: 'quantity_change',
    header: 'Qty Change',
    numeric: true,
    render: (_v, row) => (
      <span className={`text-sm font-semibold tabular-nums ${row.quantity_change > 0 ? 'text-success-600 dark:text-success-400' : 'text-danger-600 dark:text-danger-400'}`}>
        {row.quantity_change > 0 ? '+' : ''}{row.quantity_change}
      </span>
    ),
  },
  {
    key: 'type',
    header: 'Type',
    render: (_v, row) => (
      <Badge variant={row.quantity_change > 0 ? 'success' : 'warning'}>
        {row.type}
      </Badge>
    ),
  },
  {
    key: 'reason',
    header: 'Reason',
    render: (_v, row) => (
      <span className="text-sm text-neutral-600 dark:text-neutral-400">{row.reason ?? '\u2014'}</span>
    ),
  },
  {
    key: 'created_at',
    header: 'Date',
    render: (_v, row) => (
      <span className="text-sm text-neutral-500 dark:text-neutral-400 tabular-nums">
        {new Date(row.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
      </span>
    ),
  },
];

const DAYS_OF_WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const HOURS = ['6am', '9am', '12pm', '3pm', '6pm', '9pm'];

// ─── KPI Card config ─────────────────────────────────────────────────────────

interface KpiConfig {
  title: string;
  value: string;
  icon: React.ReactNode;
  gradient: string;
  iconBg: string;
  tintBg: string;
  darkTintBg: string;
  onClick?: () => void;
}

function KpiCard({ title, value, icon, gradient, iconBg, tintBg, darkTintBg, onClick }: KpiConfig) {
  const Wrapper = onClick ? 'button' : 'div';

  return (
    <Wrapper
      className={`relative overflow-hidden rounded-xl ${tintBg} ${darkTintBg} shadow-md hover:shadow-lg transition-all duration-200 p-5 group animate-slide-up ${onClick ? 'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500' : ''}`}
      onClick={onClick}
      type={onClick ? 'button' : undefined}
    >
      {/* Decorative gradient blob */}
      <div className={`absolute -top-6 -right-6 w-20 h-20 rounded-full ${gradient} opacity-20 group-hover:opacity-30 transition-opacity duration-300`} />

      <div className="relative flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-lg ${iconBg} flex items-center justify-center text-white shadow-sm`}>
          {icon}
        </div>
        <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">{title}</span>
      </div>
      <div className="relative">
        <p className="text-2xl font-bold text-neutral-900 dark:text-white tabular-nums">{value}</p>
      </div>
    </Wrapper>
  );
}

function InventoryDashboardContent() {
  const navigate = useNavigate();
  const currency = useTenantCurrency();
  const selectedLocationId = useDashboardStore((s) => s.selectedLocationId);

  // Total SKUs — active products only (is_active=true also drops drafts), the same
  // population as Stock Value, the Low Stock page and the Dashboard (QA IS1-008).
  const { data: productsData, isLoading: productsLoading, isError: productsError, refetch: refetchProducts } = useQuery({
    queryKey: ['products-count', selectedLocationId],
    queryFn: () => productService.list({ page: 1, limit: 1, location_id: selectedLocationId, is_active: 'true' }),
    staleTime: 30_000,
  });

  // Low stock — count via server-side total (accurate regardless of catalog size)
  const { data: lowStockData, isLoading: lowStockLoading, isError: lowStockError, refetch: refetchLowStock } = useQuery({
    queryKey: ['low-stock-count', selectedLocationId],
    queryFn: () =>
      productService.list({ page: 1, limit: 1, location_id: selectedLocationId, stock_status: 'low_stock', is_active: 'true' }),
    staleTime: 30_000,
  });

  // Out of stock — count via server-side total
  const { data: oosData, isLoading: oosLoading, isError: oosError, refetch: refetchOos } = useQuery({
    queryKey: ['out-of-stock-count', selectedLocationId],
    queryFn: () =>
      productService.list({ page: 1, limit: 1, location_id: selectedLocationId, stock_status: 'out_of_stock', is_active: 'true' }),
    staleTime: 30_000,
  });

  // Valuation
  const { data: valuationData, isLoading: valuationLoading, isError: valuationError, refetch: refetchValuation } = useQuery({
    queryKey: ['valuation-summary', selectedLocationId],
    queryFn: () => productService.getValuation({ location_id: selectedLocationId }),
    staleTime: 30_000,
  });

  // Stock movements
  const { data: movementsData, isLoading: movementsLoading, isError: movementsError, refetch: refetchMovements } = useQuery({
    queryKey: ['stock-movements', selectedLocationId],
    queryFn: () => productService.getStockMovements({ location_id: selectedLocationId }),
    staleTime: 30_000,
  });

  // Slow-moving stock — loaded independently so a failure here only affects its own card
  const { data: deadStockData, isLoading: deadStockLoading, isError: deadStockError } = useQuery({
    queryKey: ['dead-stock', selectedLocationId, DEAD_STOCK_DAYS],
    queryFn: () => productService.getDeadStock({ location_id: selectedLocationId, days: DEAD_STOCK_DAYS }),
    staleTime: 30_000,
  });

  const isLoading = productsLoading || lowStockLoading || oosLoading || valuationLoading || movementsLoading;
  const isError = productsError || lowStockError || oosError || valuationError || movementsError;

  const totalSkus = productsData?.meta?.total ?? 0;
  const totalValue = valuationData?.summary?.total_value ?? 0;
  const { lowStockCount, oosCount } = deriveStockKpiCounts(lowStockData, oosData);

  // Category breakdown for InventoryValueWidget — sourced from the server-side,
  // dataset-wide breakdown (not the capped current page) so it reconciles with totalValue.
  const inventoryCategories = deriveInventoryCategories(valuationData?.summary?.category_breakdown);

  // Heatmap from movements
  const movements = movementsData?.data ?? [];
  const heatmapMap = new Map<string, number>();
  movements.forEach((m) => {
    const d = new Date(m.created_at);
    const day = DAYS_OF_WEEK[d.getDay() === 0 ? 6 : d.getDay() - 1];
    const hour = d.getHours();
    let bucket = '6am';
    if (hour >= 21) bucket = '9pm';
    else if (hour >= 18) bucket = '6pm';
    else if (hour >= 15) bucket = '3pm';
    else if (hour >= 12) bucket = '12pm';
    else if (hour >= 9) bucket = '9am';
    const key = `${day}-${bucket}`;
    heatmapMap.set(key, (heatmapMap.get(key) ?? 0) + Math.abs(m.quantity_change));
  });
  const heatmapData: HeatmapCell[] = DAYS_OF_WEEK.flatMap((day) =>
    HOURS.map((hour) => ({
      row: day,
      col: hour,
      value: heatmapMap.get(`${day}-${hour}`) ?? 0,
    })),
  );

  const deadStock = deriveDeadStockSummary(deadStockData);

  const recentMovements: MovementRow[] = movements.slice(0, 10).map((m) => ({ ...m, id: m.id }));
  const status = deriveInventoryDashboardStatus({ isLoading, isError, productsTotal: totalSkus });

  if (status === 'loading') {
    return (
      <>
        {/* Skeleton header */}
        <Skeleton variant="rect" height={96} className="rounded-xl mb-6" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={120} className="rounded-xl" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <Skeleton variant="rect" height={340} className="rounded-xl" />
          <Skeleton variant="rect" height={340} className="rounded-xl" />
        </div>
        <Skeleton variant="rect" height={300} className="rounded-xl" />
      </>
    );
  }

  if (status === 'error') {
    return (
      <>
        {/* Gradient header */}
        <div className="bg-gradient-to-r from-teal-600 to-success-600 rounded-xl p-6 text-white shadow-lg mb-6 animate-fade-in">
          <h1 className="text-2xl font-bold text-white">Inventory</h1>
          <p className="text-teal-100 mt-1">Track stock levels, valuations, and movements across locations</p>
        </div>

        <EmptyState
          variant="page"
          icon={
            <svg className="h-full w-full" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          }
          title="Couldn't load inventory"
          description="Something went wrong loading your inventory data. Please try again."
          action={{
            label: 'Retry',
            onClick: () => {
              void refetchProducts();
              void refetchLowStock();
              void refetchOos();
              void refetchValuation();
              void refetchMovements();
            },
          }}
        />
      </>
    );
  }

  if (status === 'empty') {
    return (
      <>
        {/* Gradient header */}
        <div className="bg-gradient-to-r from-teal-600 to-success-600 rounded-xl p-6 text-white shadow-lg mb-6 animate-fade-in">
          <h1 className="text-2xl font-bold text-white">Inventory</h1>
          <p className="text-teal-100 mt-1">Track stock levels, valuations, and movements across locations</p>
        </div>

        <EmptyState
          variant="page"
          icon={
            <svg className="h-full w-full" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
          }
          title="No inventory data"
          description="Add products to start tracking inventory across your locations"
          action={{ label: 'Add Products', onClick: () => navigate('/products/new') }}
        />
      </>
    );
  }

  return (
    <>
      {/* Gradient header */}
      <div className="bg-gradient-to-r from-teal-600 to-success-600 rounded-xl p-6 text-white shadow-lg mb-6 animate-fade-in">
        <h1 className="text-2xl font-bold text-white">Inventory</h1>
        <p className="text-teal-100 mt-1">Overview of stock levels, valuation, and movements</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-6">
        <KpiCard
          title="Total SKUs"
          value={totalSkus.toLocaleString('en-IN')}
          gradient="bg-gradient-to-br from-teal-500 to-teal-700"
          iconBg="bg-gradient-to-br from-teal-500 to-teal-600"
          tintBg="bg-teal-50"
          darkTintBg="dark:bg-teal-900/20"
          icon={<svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>}
          onClick={() => navigate('/products')}
        />
        <KpiCard
          title="Stock Value"
          value={formatPaise(totalValue, currency)}
          gradient="bg-gradient-to-br from-success-500 to-success-700"
          iconBg="bg-gradient-to-br from-success-500 to-success-600"
          tintBg="bg-success-50"
          darkTintBg="dark:bg-success-900/20"
          icon={<svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
          onClick={() => navigate('/inventory/valuation')}
        />
        <KpiCard
          title="Low Stock"
          value={lowStockCount.toLocaleString('en-IN')}
          gradient="bg-gradient-to-br from-warning-500 to-warning-700"
          iconBg="bg-gradient-to-br from-warning-500 to-warning-600"
          tintBg="bg-warning-50"
          darkTintBg="dark:bg-warning-900/20"
          icon={<svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>}
          onClick={() => navigate(LOW_STOCK_KPI_TARGET)}
        />
        <KpiCard
          title="Out of Stock"
          value={oosCount.toLocaleString('en-IN')}
          gradient="bg-gradient-to-br from-danger-500 to-danger-700"
          iconBg="bg-gradient-to-br from-danger-500 to-danger-600"
          tintBg="bg-danger-50"
          darkTintBg="dark:bg-danger-900/20"
          icon={<svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /></svg>}
          onClick={() => navigate(OUT_OF_STOCK_KPI_TARGET)}
        />
      </div>

      {/* Charts Row — InventoryValueWidget + Heatmap */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="animate-slide-up">
          <InventoryValueWidget
            currency={currency}
            totalValue={totalValue}
            categories={inventoryCategories}
            title="Stock Value by Category"
            className="p-5 shadow-md hover:shadow-lg transition-shadow duration-200 rounded-xl"
          />
        </div>

        <Card className="p-5 shadow-md hover:shadow-lg transition-shadow duration-200 rounded-xl animate-slide-up">
          <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200 mb-3">Stock Movement Heatmap</h3>
          {movements.length > 0 ? (
            <HeatmapChart data={heatmapData} rowLabel="Day" colLabel="Time" />
          ) : (
            <p className="text-sm text-neutral-400 dark:text-neutral-500 py-8 text-center">No movement data available</p>
          )}
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {[
          { label: 'View Valuation', path: '/inventory/valuation', icon: '\u20B9' },
          { label: 'Low Stock', path: '/inventory/low-stock', icon: '\u26A0' },
          { label: 'Expiry Alerts', path: '/inventory/expiry-alerts', icon: '\u23F0' },
          { label: 'Stock Transfers', path: '/inventory/stock-transfers', icon: '\u21C4' },
        ].map((action) => (
          <Button
            key={action.path}
            variant="secondary"
            onClick={() => navigate(action.path)}
            className="justify-center shadow-sm hover:shadow-md transition-shadow duration-200"
          >
            {action.label}
          </Button>
        ))}
      </div>

      {/* Slow-moving stock */}
      <Card className="p-5 shadow-md hover:shadow-lg transition-shadow duration-200 rounded-xl animate-slide-up mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">
            Slow-Moving Stock <span className="font-normal text-neutral-400 dark:text-neutral-500">(no sales in {DEAD_STOCK_DAYS} days)</span>
          </h3>
          {!deadStockLoading && !deadStockError && (
            <div className="flex items-center gap-2">
              <Badge variant="warning">{deadStock.count.toLocaleString('en-IN')} products</Badge>
              <span className="text-sm font-semibold text-neutral-900 dark:text-white tabular-nums">
                {formatPaise(deadStock.totalValue, currency)}
              </span>
            </div>
          )}
        </div>
        {deadStockLoading ? (
          <Skeleton variant="rect" height={120} className="rounded-lg" />
        ) : deadStockError ? (
          <p className="text-sm text-danger-600 dark:text-danger-400 py-6 text-center">Couldn't load slow-moving stock</p>
        ) : (
          <DataTable<DeadStockItem>
            columns={[
              {
                key: 'name',
                header: 'Product',
                render: (_v, row) => (
                  <div>
                    <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{row.name}</p>
                    <p className="text-xs text-neutral-400 dark:text-neutral-500">{row.sku}</p>
                  </div>
                ),
              },
              {
                key: 'stock_quantity',
                header: 'Qty',
                numeric: true,
                render: (_v, row) => <span className="text-sm tabular-nums">{Number(row.stock_quantity)}</span>,
              },
              {
                key: 'stock_value',
                header: 'Stock Value',
                numeric: true,
                render: (_v, row) => (
                  <span className="text-sm font-semibold tabular-nums">{formatPaise(Number(row.stock_value), currency)}</span>
                ),
              },
              {
                key: 'days_since_sale',
                header: 'Last Sold',
                render: (_v, row) =>
                  row.days_since_sale === null ? (
                    <Badge variant="danger">Never sold</Badge>
                  ) : (
                    <span className="text-sm text-neutral-500 dark:text-neutral-400 tabular-nums">{row.days_since_sale} days ago</span>
                  ),
              },
            ]}
            data={deadStock.topRows}
            variant="compact"
            emptyState={{ title: 'No slow-moving stock', description: `Every in-stock product sold in the last ${DEAD_STOCK_DAYS} days` }}
          />
        )}
      </Card>

      {/* Recent Stock Movements */}
      <Card className="p-5 shadow-md hover:shadow-lg transition-shadow duration-200 rounded-xl animate-slide-up">
        <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200 mb-3">Recent Stock Movements</h3>
        <DataTable<MovementRow>
          columns={movementColumns}
          data={recentMovements}
          variant="compact"
          emptyState={{ title: 'No stock movements yet', description: 'Movements will appear as products are bought and sold' }}
        />
      </Card>
    </>
  );
}

export function InventoryDashboardPage() {
  return (
    <PageContainer
      title="Inventory"
      breadcrumbs={[{ label: 'Inventory' }]}
    >
      <ErrorBoundary resetKey="inventory-dashboard">
        <InventoryDashboardContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
